/** Modern management dashboard for searching, sorting, cloning and editing forms. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUiLocale } from '../../app/ui-locale';
import { cloneForm, getFormsList } from '../../api/forms-api';
import { ErrorMessage, Loading } from '../../components/Loading';
import { useFeedback } from '../../components/Feedback';
import type { FormListItem, FormListQuery } from '../../models/form';
import { DynamicFormsPermission } from '../../models/access-control';
import { useAccess } from '../access/AccessContext';
import { AdminIcon } from '../management/AdminIcon';

const defaultQuery: FormListQuery = {
  Term: '', PageIndex: 1, PageSize: 10, OnlyEnable: true, OnlyWithSubmitCount: false, NotExpired: false, SortType: 'DESC', SortBy: 'FormID'
};

const COPY = {
  fa: {
    eyebrow: 'داشبورد مدیریتی', title: 'فرم‌های شما در یک نگاه', description: 'فرم‌ها را جستجو، مدیریت و برای دریافت پاسخ آماده کنید.',
    newForm: 'ایجاد فرم جدید', visibleForms: 'فرم‌های نمایش‌داده‌شده', activeForms: 'فرم فعال', responses: 'پاسخ ثبت‌شده', limitedForms: 'دارای ظرفیت محدود',
    searchPlaceholder: 'جستجو در عنوان فرم‌ها…', sortBy: 'مرتب‌سازی', sortDirection: 'جهت مرتب‌سازی', pageSize: 'تعداد در صفحه', refresh: 'بازخوانی',
    clearFilters: 'پاک کردن فیلترها', onlyActive: 'فقط فرم‌های فعال', onlyWithResponse: 'فقط دارای پاسخ', onlyNotExpired: 'فقط منقضی‌نشده',
    id: 'شناسه', formTitle: 'عنوان', responseCount: 'تعداد ثبت', createdOn: 'تاریخ ایجاد', startDate: 'تاریخ شروع', endDate: 'تاریخ پایان',
    descending: 'نزولی', ascending: 'صعودی', perPage: 'در صفحه', createCardTitle: 'ساخت فرم تازه', createCardText: 'فرم جدیدی را از صفر طراحی کنید.',
    edit: 'ویرایش', clone: 'تکثیر', cloning: 'در حال تکثیر…', active: 'فعال', inactive: 'غیرفعال', noDescription: 'توضیحی برای این فرم ثبت نشده است.',
    capacity: 'ظرفیت', unlimited: 'نامحدود', schedule: 'بازه فعالیت', noSchedule: 'بدون محدودیت زمانی', emptyTitle: 'فرمی با این فیلترها پیدا نشد.',
    emptyText: 'فیلترها را پاک کنید یا یک فرم جدید بسازید.', previous: 'صفحه قبل', next: 'صفحه بعد', page: 'صفحه',
    portalForms: 'تعداد فرم‌های این پورتال', of: 'از', capacityReached: ' — ظرفیت ایجاد فرم جدید تکمیل شده است.',
    cloneConfirm: (title: string) => `یک کپی از فرم «${title}» ایجاد شود؟`, cloneSuccess: 'فرم تکثیر شد.', cloneError: 'خطا در تکثیر فرم'
  },
  en: {
    eyebrow: 'Management dashboard', title: 'Your forms at a glance', description: 'Search, manage and prepare forms to collect submissions.',
    newForm: 'Create new form', visibleForms: 'Visible forms', activeForms: 'Active forms', responses: 'Submissions', limitedForms: 'Limited capacity',
    searchPlaceholder: 'Search form titles…', sortBy: 'Sort by', sortDirection: 'Direction', pageSize: 'Page size', refresh: 'Refresh',
    clearFilters: 'Clear filters', onlyActive: 'Active forms only', onlyWithResponse: 'With submissions only', onlyNotExpired: 'Not expired only',
    id: 'ID', formTitle: 'Title', responseCount: 'Submissions', createdOn: 'Created on', startDate: 'Start date', endDate: 'End date',
    descending: 'Descending', ascending: 'Ascending', perPage: 'per page', createCardTitle: 'Create a new form', createCardText: 'Start a fresh form from scratch.',
    edit: 'Edit', clone: 'Duplicate', cloning: 'Duplicating…', active: 'Active', inactive: 'Inactive', noDescription: 'No description has been provided for this form.',
    capacity: 'Capacity', unlimited: 'Unlimited', schedule: 'Schedule', noSchedule: 'No date restriction', emptyTitle: 'No forms match these filters.',
    emptyText: 'Clear the filters or create a new form.', previous: 'Previous', next: 'Next', page: 'Page',
    portalForms: 'Forms in this portal', of: 'of', capacityReached: ' — The new-form limit has been reached.',
    cloneConfirm: (title: string) => `Create a duplicate of “${title}”?`, cloneSuccess: 'Form duplicated.', cloneError: 'Unable to duplicate the form'
  },
  fr: {
    eyebrow: 'Tableau de bord', title: 'Vos formulaires en un coup d’œil', description: 'Recherchez, gérez et préparez vos formulaires pour recueillir des réponses.',
    newForm: 'Créer un formulaire', visibleForms: 'Formulaires affichés', activeForms: 'Formulaires actifs', responses: 'Réponses reçues', limitedForms: 'Capacité limitée',
    searchPlaceholder: 'Rechercher dans les titres des formulaires…', sortBy: 'Trier par', sortDirection: 'Ordre', pageSize: 'Éléments par page', refresh: 'Actualiser',
    clearFilters: 'Effacer les filtres', onlyActive: 'Formulaires actifs uniquement', onlyWithResponse: 'Avec réponses uniquement', onlyNotExpired: 'Non expirés uniquement',
    id: 'ID', formTitle: 'Titre', responseCount: 'Réponses', createdOn: 'Créé le', startDate: 'Date de début', endDate: 'Date de fin',
    descending: 'Décroissant', ascending: 'Croissant', perPage: 'par page', createCardTitle: 'Créer un nouveau formulaire', createCardText: 'Concevez un nouveau formulaire à partir de zéro.',
    edit: 'Modifier', clone: 'Dupliquer', cloning: 'Duplication…', active: 'Actif', inactive: 'Inactif', noDescription: 'Aucune description n’a été renseignée pour ce formulaire.',
    capacity: 'Capacité', unlimited: 'Illimitée', schedule: 'Période d’activité', noSchedule: 'Aucune restriction de date', emptyTitle: 'Aucun formulaire ne correspond à ces filtres.',
    emptyText: 'Effacez les filtres ou créez un nouveau formulaire.', previous: 'Précédent', next: 'Suivant', page: 'Page',
    portalForms: 'Formulaires de ce portail', of: 'sur', capacityReached: ' — La limite de création de nouveaux formulaires est atteinte.',
    cloneConfirm: (title: string) => `Créer une copie du formulaire « ${title} » ?`, cloneSuccess: 'Le formulaire a été dupliqué.', cloneError: 'Impossible de dupliquer le formulaire'
  }
} as const;

function safePositive(value: string | undefined, fallback: number): number {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : fallback;
}

export function FormListPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const { access, hasPermission } = useAccess();
  const { locale, formatNumber, formatDate } = useUiLocale();
  const copy = COPY[locale];
  const [query, setQuery] = useState<FormListQuery>(() => ({
    ...defaultQuery,
    PageSize: safePositive(params.pagesize, defaultQuery.PageSize),
    PageIndex: safePositive(params.pageindex, defaultQuery.PageIndex)
  }));
  const [items, setItems] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [reloadToken, setReloadToken] = useState(0);
  const [cloningIds, setCloningIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const pageSize = safePositive(params.pagesize, defaultQuery.PageSize);
    const pageIndex = safePositive(params.pageindex, defaultQuery.PageIndex);
    setQuery((current) => current.PageSize === pageSize && current.PageIndex === pageIndex ? current : { ...current, PageSize: pageSize, PageIndex: pageIndex });
  }, [params.pageindex, params.pagesize]);

  useEffect(() => {
    const expected = `/dynamicforms/dashboard/${query.PageSize}/${query.PageIndex}`;
    navigate(expected, { replace: true });
  }, [navigate, query.PageIndex, query.PageSize]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(undefined);
      getFormsList(query, controller.signal)
        .then(setItems)
        .catch((loadError) => { if (!(loadError instanceof DOMException && loadError.name === 'AbortError')) setError(loadError); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, query.Term ? 350 : 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query, reloadToken]);

  useEffect(() => {
    if (!loading && !error && query.PageIndex > 1 && items.length === 0) {
      setQuery((current) => ({ ...current, PageIndex: Math.max(1, current.PageIndex - 1) }));
    }
  }, [error, items.length, loading, query.PageIndex]);

  const patchQuery = useCallback((changes: Partial<FormListQuery>) => {
    setQuery((current) => ({ ...current, ...changes, PageIndex: changes.PageIndex ?? 1 }));
  }, []);

  const activeFilterCount = useMemo(() =>
    Number(query.OnlyEnable !== defaultQuery.OnlyEnable)
    + Number(query.OnlyWithSubmitCount !== defaultQuery.OnlyWithSubmitCount)
    + Number(query.NotExpired !== defaultQuery.NotExpired)
    + Number(Boolean(query.Term.trim())), [query]);
  const resetFilters = () => setQuery({ ...defaultQuery, PageSize: query.PageSize });

  const stats = useMemo(() => ({
    forms: items.length,
    active: items.filter((item) => item.IsEnable).length,
    responses: items.reduce((total, item) => total + Number(item.SubmitCount || 0), 0),
    limited: items.filter((item) => item.HaveCapacity).length
  }), [items]);

  const clone = async (form: FormListItem) => {
    if (!window.confirm(copy.cloneConfirm(form.Title))) return;
    setCloningIds((current) => new Set(current).add(form.FormID));
    try {
      await cloneForm(form.FormID);
      notify(copy.cloneSuccess, 'success');
      setReloadToken((value) => value + 1);
    } catch (cloneError) {
      notify(cloneError instanceof Error ? cloneError.message : copy.cloneError, 'error');
    } finally {
      setCloningIds((current) => { const next = new Set(current); next.delete(form.FormID); return next; });
    }
  };

  const scheduleText = (form: FormListItem) => form.IsInfinite
    ? copy.noSchedule
    : `${formatDate(form.StartDate)} – ${formatDate(form.EndDate)}`;

  return <section className="df-admin-dashboard-page">
    <header className="df-admin-hero">
      <div className="df-admin-hero-copy">
        <span className="df-admin-eyebrow">{copy.eyebrow}</span>
        <h1>{copy.title}</h1>
        <p>{copy.description}</p>
      </div>
      {access.CanCreateForms ? <Link className="df-admin-button df-admin-button--primary df-admin-button--large" to="/dynamicforms/forms/add"><AdminIcon name="plus"/>{copy.newForm}</Link> : null}
    </header>

    {access.MaxForms != null ? <div className={`alert ${access.FormCount >= access.MaxForms ? 'alert-warning' : 'alert-info'}`}>{copy.portalForms}: <strong>{formatNumber(access.FormCount)}</strong> {copy.of} <strong>{formatNumber(access.MaxForms)}</strong>{access.FormCount >= access.MaxForms ? copy.capacityReached : ''}</div> : null}

    <div className="df-admin-stat-grid" aria-label={copy.eyebrow}>
      <article className="df-admin-stat-card is-indigo"><span className="df-admin-stat-icon"><AdminIcon name="forms"/></span><div><strong>{formatNumber(stats.forms)}</strong><span>{copy.visibleForms}</span></div></article>
      <article className="df-admin-stat-card is-emerald"><span className="df-admin-stat-icon"><AdminIcon name="active"/></span><div><strong>{formatNumber(stats.active)}</strong><span>{copy.activeForms}</span></div></article>
      <article className="df-admin-stat-card is-sky"><span className="df-admin-stat-icon"><AdminIcon name="responses"/></span><div><strong>{formatNumber(stats.responses)}</strong><span>{copy.responses}</span></div></article>
      <article className="df-admin-stat-card is-amber"><span className="df-admin-stat-icon"><AdminIcon name="capacity"/></span><div><strong>{formatNumber(stats.limited)}</strong><span>{copy.limitedForms}</span></div></article>
    </div>

    <section className="df-admin-filter-card">
      <div className="df-admin-filter-row">
        <label className="df-admin-search-control">
          <AdminIcon name="search" size={19}/>
          <input aria-label={copy.searchPlaceholder} placeholder={copy.searchPlaceholder} value={query.Term} onChange={(event) => patchQuery({ Term: event.target.value })} />
          {query.Term ? <button type="button" onClick={() => patchQuery({ Term: '' })} aria-label={copy.clearFilters}>×</button> : null}
        </label>
        <label className="df-admin-select-control"><span>{copy.sortBy}</span><select value={query.SortBy} onChange={(event) => patchQuery({ SortBy: event.target.value })}><option value="FormID">{copy.id}</option><option value="Title">{copy.formTitle}</option><option value="SubmitCount">{copy.responseCount}</option><option value="CreatedOn">{copy.createdOn}</option><option value="StartDate">{copy.startDate}</option><option value="EndDate">{copy.endDate}</option></select></label>
        <label className="df-admin-select-control"><span>{copy.sortDirection}</span><select value={query.SortType} onChange={(event) => patchQuery({ SortType: event.target.value as FormListQuery['SortType'] })}><option value="DESC">{copy.descending}</option><option value="ASC">{copy.ascending}</option></select></label>
        <label className="df-admin-select-control"><span>{copy.pageSize}</span><select value={query.PageSize} onChange={(event) => patchQuery({ PageSize: Number(event.target.value) })}>{[10, 20, 30, 50].map((size) => <option value={size} key={size}>{formatNumber(size)} {copy.perPage}</option>)}</select></label>
        <button type="button" className="df-admin-button df-admin-button--secondary" onClick={() => setReloadToken((value) => value + 1)}><AdminIcon name="refresh" size={18}/>{copy.refresh}</button>
      </div>
      <div className="df-admin-filter-footer">
        <div className="df-admin-check-list">
          <label><input type="checkbox" checked={query.OnlyEnable} onChange={(event) => patchQuery({ OnlyEnable: event.target.checked })} /><span>{copy.onlyActive}</span></label>
          <label><input type="checkbox" checked={query.OnlyWithSubmitCount} onChange={(event) => patchQuery({ OnlyWithSubmitCount: event.target.checked })} /><span>{copy.onlyWithResponse}</span></label>
          <label><input type="checkbox" checked={query.NotExpired} onChange={(event) => patchQuery({ NotExpired: event.target.checked })} /><span>{copy.onlyNotExpired}</span></label>
        </div>
        {activeFilterCount ? <button type="button" className="df-admin-text-button" onClick={resetFilters}>{copy.clearFilters} <span>{formatNumber(activeFilterCount)}</span></button> : null}
      </div>
    </section>

    {error ? <ErrorMessage error={error} /> : null}
    {loading && !items.length ? <Loading /> : <>
      <div className={`df-admin-form-grid ${loading ? 'is-refreshing' : ''}`} aria-busy={loading}>
        {access.CanCreateForms ? <Link to="/dynamicforms/forms/add" className="df-admin-create-card">
          <span className="df-admin-create-card-icon"><AdminIcon name="plus" size={28}/></span>
          <strong>{copy.createCardTitle}</strong><p>{copy.createCardText}</p>
        </Link> : null}
        {items.map((form) => <article className="df-admin-form-card" key={form.FormID}>
          <div className="df-admin-form-card-head">
            <span className={`df-admin-status-pill ${form.IsEnable ? 'is-active' : 'is-inactive'}`}><i/>{form.IsEnable ? copy.active : copy.inactive}</span>
            {hasPermission(DynamicFormsPermission.EditForms, form.FormID) ? <div className="df-admin-card-actions">
              <Link title={copy.edit} aria-label={copy.edit} to={`/dynamicforms/forms/edit/${form.FormID}`}><AdminIcon name="edit" size={18}/></Link>
              {access.CanCloneForms ? <button type="button" title={copy.clone} aria-label={copy.clone} disabled={cloningIds.has(form.FormID)} onClick={() => void clone(form)}><AdminIcon name="copy" size={18}/></button> : null}
            </div> : null}
          </div>
          <div className="df-admin-form-card-body">
            <h2>{form.Title}</h2>
            <p>{form.Description || copy.noDescription}</p>
            <div className="df-admin-form-metrics">
              {hasPermission(DynamicFormsPermission.ViewSubmissions, form.FormID) ? <div><span><AdminIcon name="responses" size={17}/>{copy.responses}</span><strong>{formatNumber(form.SubmitCount)}</strong></div> : null}
              <div><span><AdminIcon name="capacity" size={17}/>{copy.capacity}</span><strong>{form.HaveCapacity ? `${formatNumber(form.SubmitCount)}/${formatNumber(form.Capacity)}` : copy.unlimited}</strong></div>
            </div>
          </div>
          <footer className="df-admin-form-card-footer">
            <span><AdminIcon name="calendar" size={16}/>{scheduleText(form)}</span>
            <span className="df-admin-form-id">#{formatNumber(form.FormID)}</span>
          </footer>
        </article>)}
      </div>
      {!items.length ? <div className="df-admin-empty-state"><span><AdminIcon name="search" size={30}/></span><strong>{copy.emptyTitle}</strong><p>{copy.emptyText}</p><button type="button" className="df-admin-button df-admin-button--secondary" onClick={resetFilters}>{copy.clearFilters}</button></div> : null}
    </>}

    <nav className="df-admin-pager" aria-label={copy.page}>
      <button type="button" disabled={query.PageIndex <= 1 || loading} onClick={() => patchQuery({ PageIndex: query.PageIndex - 1 })}><AdminIcon name={locale === 'en' ? 'chevron-left' : 'chevron-right'} size={18}/>{copy.previous}</button>
      <span>{copy.page} <strong>{formatNumber(query.PageIndex)}</strong></span>
      <button type="button" disabled={items.length < query.PageSize || loading} onClick={() => patchQuery({ PageIndex: query.PageIndex + 1 })}>{copy.next}<AdminIcon name={locale === 'en' ? 'chevron-right' : 'chevron-left'} size={18}/></button>
    </nav>
  </section>;
}
